import { useEffect, useMemo, useState } from "react";

import { useIsFetching, useQuery } from "@tanstack/react-query";
import { stripAllScriptTags } from "@v2/utils/dom";
import { transformNextImageToImg } from "replo-utils/lib/html";
import {
  getLayoutCssUrl,
  getPageHtmlUrl,
} from "replo-utils/lib/nextJSProjectCache";

async function getCachedPageContent({
  projectId,
  pageRoute,
}: {
  projectId: string;
  pageRoute: string;
}) {
  const url = getPageHtmlUrl(projectId, pageRoute);
  const response = await fetch(url);
  if (!response.ok) {
    if (response.status === 404) {
      return "";
    }
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(`Failed to fetch page content from ${url}`);
  }
  const html = await response.text();
  return html;
}

async function getCachedLayoutCss({ projectId }: { projectId: string }) {
  const url = getLayoutCssUrl(projectId);
  const response = await fetch(url);
  if (!response.ok) {
    if (response.status === 404) {
      return "";
    }
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(`Failed to fetch layout css from ${url}`);
  }
  const css = await response.text();
  return css;
}

const CACHED_PAGE_CONTENT_QUERY_KEY = "cachedPageContent";

/**
 * This is a utility function to check if a page is a 404 page.
 * @param html - The html content of the page.
 * @returns True if the page is a 404 page, false otherwise. NOTE: this page
 * must have had it's scripts stripped before calling this function - this is
 * because next.js includes 404 content in a js object inside a script tag even
 * if the page itself is not a 404.
 */
function is404Page(html: string) {
  return html.includes("404") && html.includes("This page could not be found.");
}

/**
 * This is a hook that can be used to get the static html content for a page.
 * Data will only be returned if this page has previously been rendered for this
 * project.
 *
 * NOTE (Gabe 2025-08-15): This hook doesn't output html content until all
 * active instances of this hook have returned data. This is to prevent a list
 * of previews from rendering 1 by 1, and instead have them all render at once.
 */
export const useCachedPageContent = ({
  pageRoute,
  projectId,
}: {
  pageRoute: string;
  projectId?: string;
}): { isLoading: boolean; html: string | undefined } => {
  // NOTE (Gabe 2025-08-15): Because we block returning of data if any cached
  // page content query is fetching, we need to use hasReturned to ensure that
  // if this instance of the hook has ever returned data then it will continue
  // to return data. Otherwise, a list of previews that were previously rendered
  // will be hidden by a subsequent instance of the hook that starts fetching.
  const [hasReturned, setHasReturned] = useState(false);

  const isAnyCachedContentFetching =
    useIsFetching({ queryKey: [CACHED_PAGE_CONTENT_QUERY_KEY] }) > 0;

  const { data: html, isLoading: isLoadingCachedPage } = useQuery({
    staleTime: 1000 * 10,
    queryKey: [CACHED_PAGE_CONTENT_QUERY_KEY, projectId, pageRoute],
    queryFn: async () => {
      if (!projectId) {
        return undefined;
      }

      const [css, html] = await Promise.all([
        getCachedLayoutCss({ projectId }),
        getCachedPageContent({ projectId, pageRoute }),
      ]);
      let transformedHtml = html;
      if (html) {
        transformedHtml = stripAllScriptTags(html);
      }
      if (is404Page(transformedHtml)) {
        return "";
      }
      if (!css || !transformedHtml) {
        return "";
      }

      transformedHtml = transformNextImageToImg(
        html.replace(
          /<link rel="stylesheet" href="([^"]+)"[^>]*>/,
          `<style>${css}</style>`,
        ),
      );

      return transformedHtml;
    },
    enabled: Boolean(projectId),
    retry: false,
    refetchOnWindowFocus: false,
  });
  // NOTE (Gabe 2025-08-15): There is a race condition where
  // isAnyCachedContentFetching can be false but data is not actually available
  // for this page.
  const isLoading = isLoadingCachedPage || isAnyCachedContentFetching;

  const returnValue = useMemo(() => {
    if (!isLoading || hasReturned) {
      return { html, isLoading: false };
    }
    return { html: undefined, isLoading: true };
  }, [hasReturned, html, isLoading]);

  useEffect(() => {
    if (returnValue) {
      setHasReturned(true);
    }
  }, [returnValue]);

  return returnValue;
};
